import { Shield, Users, Share2, LayoutTemplate, Palette, CreditCard, ArrowRight, Settings, Sparkles } from 'lucide-react';
import { CardDashboardItem, PageLayout, PageHeader, PageTitle, SectionHeader } from '@/util-components/page-layout';
import { usePermissions } from '@/lib/hooks/use-permissions';
import { Permission } from '@/lib/api/interfaces/permissions.interface';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader } from '@/ui-components/ui/card';
import { Button } from '@/ui-components/ui/button';
import { cn } from '@/lib/utils/cn';
import { toast } from "@/lib/toast";

/**
 * OrganizationDashboard
 * 
 * The main entry point for the "Verein" section.
 * Displays a dashboard of available management modules, organized in sections.
 */
export const OrganizationDashboard = () => {
  const { can } = usePermissions();
  const navigate = useNavigate();

  const brandingModules: CardDashboardItem[] = [
    {
      title: "Vereinsdaten & Branding",
      description: "Verwalten Sie Logos, Farben und allgemeine Informationen Ihres Vereins.",
      icon: Palette,
      path: "/vereine/branding",
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-100"
    },
    {
      title: "Standard-Temlates",
      description: "Definieren Sie Standard-Templates für Geburtstage und Spiele für schnellere Posts.",
      icon: LayoutTemplate,
      path: "/post-generator/default-templates",
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-100",
      disabled: !can(Permission.MANAGE_ENTITIES),
      disabledMessage: "Diese Funktion ist nicht für dich verfügbar."
    },
    {
      title: "Integrationen",
      description: "Verbinden Sie Social Media Accounts für automatisches Posting.",
      icon: Share2,
      path: "/integrations",
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-100",
      disabled: !can(Permission.MANAGE_INTEGRATIONS),
      disabledMessage: "Diese Funktion ist nicht für dich verfügbar."
    },
    {
      title: "KI Anpassung",
      description: "Steuern Sie automatische Captions und Stilvorgaben",
      icon: Sparkles,
      path: "/vereine/ki-anpassung",
      color: "text-cyan-700",
      bgColor: "bg-cyan-50",
      borderColor: "border-cyan-100",
      disabled: !can(Permission.GENERATE_AI_CAPTIONS),
      disabledMessage: "KI Anpassung ist im Pro- oder Custom-Plan verfügbar."
    },
  ];

  const managementModules: CardDashboardItem[] = [
    {
      title: "Mitglieder & Rollen",
      description: "Laden Sie Teammitglieder ein und verwalten Sie Zugriffsberechtigungen.",
      icon: Users,
      path: "/users",
      color: "text-indigo-600",
      bgColor: "bg-indigo-50",
      borderColor: "border-indigo-100",
      disabled: !can(Permission.MANAGE_ORGANIZATION),
      disabledMessage: "Diese Funktion ist nicht für dich verfügbar."
    },
    {
      title: "Abonnement",
      description: "Verwalten Sie Ihr Abo, Rechnungen und Zahlungsdaten.",
      icon: CreditCard,
      path: "/vereine/billing",
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-100",
      disabled: !can(Permission.MANAGE_BILLING),
      disabledMessage: "Diese Funktion ist nur für einen Vereinsadmin verfügbar."
    },
    {
      title: "Erweiterte Funktionen",
      description: "Verwalten Sie erweiterte Funktionen und Einstellungen für Ihren Verein.",
      icon: Shield,
      path: "/vereine/advanced-features",
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-100",
      disabled: !can(Permission.MANAGE_ORGANIZATION),
      disabledMessage: "Diese Funktion ist nicht für dich verfügbar."
    }
  ];

  const renderModuleCard = (item: CardDashboardItem) => {
    const isDisabled = Boolean(item.disabled);
    const bgColor = item.bgColor ?? "bg-blue-50";
    const borderColor = item.borderColor ?? "border-blue-100";
    const color = item.color ?? "text-blue-600";

    return (
      <Card
        key={item.path}
        className={cn(
          "group relative overflow-hidden border-gray-200 hover:border-blue-200 hover:shadow-md transition-all",
          isDisabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"
        )}
        onClick={() => {
          if (!isDisabled) {
            navigate(item.path);
          } else if (item.disabledMessage) {
            toast.error(item.disabledMessage);
          }
        }}
      >
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div
              className={cn(
                "h-12 w-12 rounded-2xl border flex items-center justify-center",
                bgColor,
                borderColor
              )}
            >
              <item.icon className={cn("h-6 w-6", color)} />
            </div>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "text-gray-400 group-hover:text-blue-600 -mr-2 -mt-2",
                isDisabled ? "pointer-events-none" : undefined
              )}
            >
              <ArrowRight className="h-5 w-5" />
            </Button>
          </div>
          <div className="mt-4">
            <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
              {item.title}
            </h3>
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {item.description}
            </p>
          </div>
        </CardHeader>
      </Card>
    );
  };

  return (
    <PageLayout basePath="/vereine">
      <PageHeader>
        <PageTitle 
          title="Dein Verein" 
          icon={Shield} 
          iconColor="text-blue-600" 
          iconBg="bg-blue-50" 
        />
      </PageHeader>

      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full space-y-10">
        
        {/* Section 1: Branding & Content */}
        <div className="space-y-6">
          <SectionHeader 
            icon={Palette} 
            title="Design & Inhalte" 
            description="Verwalten Sie das Erscheinungsbild und die Templates für Ihren Verein."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {brandingModules.map(renderModuleCard)}
          </div>
        </div>

        {/* Section 2: Management */}
        <div className="space-y-6">
          <SectionHeader 
            icon={Settings} 
            title="Verwaltung & Einstellungen" 
            description="Organisatorische Einstellungen, Mitgliederverwaltung und Abonnements."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {managementModules.map(renderModuleCard)}
          </div>
        </div>

      </div>
    </PageLayout>
  );
};

export default OrganizationDashboard;
